// Npm
import { Header as XPSHeader, DropdownMenu, Loader, MenuItem, Popover } from '@nt/dds-react';

// Local
import { useOktaAuth } from '@okta/okta-react';
import React, { FC, lazy, ReactElement, Suspense, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { resetCreate } from '../../pages/Create/CreateSlice';
import { resetUserDetails, selectUserDetails, setUserLoggedOut } from '../../pages/Home/HomeSlice';
import { resetLegalEntity } from '../../pages/LegalEntity/LegalEntitySlice';
import oktaConfig from '../../routes/config';
import { last, ORIGIN_MAPPING } from '../../utils/helper';
import { FormatMessage, IUserDetails, OktaConfiguration, OktaOicdProperties } from '../../utils/types';
import { formatMessageHOC } from '../Hoc/formatMessageHOC';
import './Header.css';

const Link = lazy(() => import('react-router-dom').then(module => ({ default: module.Link })));
const Logo = lazy(() => import('@nt/dds-react').then(module => ({ default: module.Logo })));
const Modal = lazy(() => import('../common/Modal'));

interface Props extends FormatMessage {}

/**
 * @param {*} {f: FormatMessage}
 * @return {ReactElement}
 */
const Header: FC<Props> = ({ f }): ReactElement => {
  const dispatch = useAppDispatch();
  const { oktaAuth } = useOktaAuth();
  const userDetails: IUserDetails = useAppSelector(selectUserDetails);
  const {
    config: {
      okta: { oidc: OIDConfigs }
    }
  } = oktaConfig as OktaConfiguration;
  const oktaEnv = ORIGIN_MAPPING[last].okta;

  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const getContactInfo = () => {
    return (
      <div className="help-details-wrap">
        <h6 className="fontweight-500 roboto-font help-heading">{f({ id: 'home.helpicon.heading' })}</h6>
        <ul className="help-details-list">
          <li className="fontweight-500 roboto-font help-details-list-item">
            {f({ id: 'home.helpIcon.sampleNumber.title' })} {f({ id: 'home.helpIcon.sampleNumber' })}
          </li>
          <li className="fontweight-500 roboto-font help-details-list-item">
            {f({ id: 'home.helpDescription.email.title' })}{' '}
            <span className="underline">{f({ id: 'home.helpDescription.email' })}</span>
          </li>
        </ul>
      </div>
    );
  };

  const logoutFn = async () => {
    dispatch(resetUserDetails());
    dispatch(resetLegalEntity());
    dispatch(resetCreate());
    const oktaUrl = OIDConfigs[oktaEnv as keyof OktaOicdProperties].oktaUrl as string;
    oktaAuth.revokeAccessToken();
    oktaAuth.revokeRefreshToken();
    oktaAuth.tokenManager.remove('idToken');
    oktaAuth.tokenManager.remove('accessToken');
    oktaAuth.signOut({
      clearTokensBeforeRedirect: true,
      postLogoutRedirectUri: oktaUrl
    });
  };

  const headerLinks = () => {
    return (
      <div className="header-content-wrap">
        <div className="header-profile-wrap">
          <DropdownMenu
            buttonKind="borderless"
            buttonSize="small"
            buttonText={userDetails?.name}
            className=""
            id="menu1"
          >
            <MenuItem onClick={() => setModalOpen(true)} text="Exit AOR" />
          </DropdownMenu>
        </div>
        <div className="header-content-notification-wrap">
          {/* <div className="notifications-icon-wrap">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M17.9994 16.4976L20.0154 18.5126V19.4976H3.98438V18.5126L5.99938 16.4966V11.5276C5.99938 8.43461 7.64038 5.90261 10.4994 5.19961V4.49561C10.4994 3.65161 11.1554 2.99561 11.9994 2.99561C12.8434 2.99561 13.4994 3.65261 13.4994 4.49561V5.19961C16.3594 5.90261 17.9994 8.48061 17.9994 11.5286V16.4966V16.4976ZM11.9994 22.4976C10.8744 22.4976 9.98338 21.6066 9.98338 20.5286H14.0154C14.0154 21.6066 13.0774 22.4976 11.9994 22.4976Z"
                fill="white"
              />
            </svg>

            <div className="notification-badge">1</div>
          </div> */}
          {/* <div className="notifications-icon-wrap">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M14.6178 2.5127H3H2.993C1.892 2.5127 1 3.4047 1 4.5057V4.5127V16.5127C1 17.6127 1.9 18.5127 3 18.5127H10V20.5127H8.001V22.5117H16V20.5127H14.001V18.5127H21.001C22.101 18.5127 23.001 17.6127 23.001 16.5127V10.4608C22.3092 10.3885 21.6395 10.242 21 10.0293V16.5127H3V4.5127H15.1655C14.9134 3.87684 14.7279 3.2073 14.6178 2.5127Z"
                fill="white"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M14.0214 6.84046C14.1295 6.57521 14.4401 6.44439 14.7157 6.54845L14.7157 6.54837C14.7385 6.55698 14.7604 6.56692 14.7812 6.57808C14.9727 6.64209 15.1297 6.79212 15.1892 6.99412L16.3903 11.0693H18.9643C19.2603 11.0693 19.5001 11.3002 19.5001 11.5851C19.5001 11.8701 19.2603 12.1009 18.9643 12.1009H16.1786C16.1568 12.1009 16.1353 12.0997 16.1142 12.0973C16.0883 12.108 16.0614 12.1173 16.0335 12.1249C15.6919 12.2183 15.3365 12.0275 15.2396 11.6987L14.3807 8.78445L12.2619 13.9902C12.2646 14.1933 12.1416 14.3874 11.9352 14.4717C11.6629 14.5828 11.3485 14.4602 11.233 14.198L9.36695 9.95699L8.40079 11.8262C8.28963 12.0414 8.04319 12.1489 7.80977 12.1009H5.03572C4.73976 12.1009 4.5 11.8701 4.5 11.5851C4.5 11.3002 4.73976 11.0693 5.03572 11.0693H7.59505L8.87357 8.59582C9.00528 8.34079 9.32694 8.23674 9.59181 8.36368V8.36371L9.59826 8.36684C9.7561 8.40467 9.89422 8.51099 9.96264 8.66627L11.6848 12.5806L13.9414 7.03707C13.9561 6.96878 13.9828 6.90365 14.0198 6.84438L14.0214 6.84046Z"
                fill="white"
              />
            </svg>

            <div className="history-badge">
              <Icon ariaHidden name="check_circle" type="info" />
            </div>
          </div> */}
          <div className="notifications-icon-wrap">
            <Popover
              arrow
              content={getContactInfo()}
              direction="left"
              error={undefined}
              id="help_popover"
              overflowDirection=""
              overflowWidth=""
              warning
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                aria-label="Help"
                width="24"
                height="25"
                viewBox="0 0 24 25"
                fill="none"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M11 18.5127H12.999V16.5127H10.999V18.5127H11ZM12 2.5127C6.48 2.5127 2 6.9927 2 12.5127C2 18.0327 6.48 22.5127 12 22.5127C17.52 22.5127 22 18.0327 22 12.5127C22 6.9927 17.52 2.5127 12 2.5127ZM12 20.5127C7.59 20.5127 4 16.9227 4 12.5127C4 8.1027 7.59 4.5127 12 4.5127C16.41 4.5127 20 8.1027 20 12.5127C20 16.9227 16.41 20.5127 12 20.5127ZM12 6.5127C12 6.5127 12 6.5127 11.999 6.5127C9.79 6.5127 8 8.3027 8 10.5117V10.5127H10C10 9.4127 10.9 8.5127 12 8.5127C13.1 8.5127 14 9.4127 14 10.5127C14 12.5127 11 12.2627 11 15.5127H13C13 13.2627 16 13.0127 16 10.5127C16 10.5127 16 10.5127 16 10.5117C16 8.3027 14.21 6.5127 12.001 6.5127H12Z"
                  fill="white"
                />
              </svg>
            </Popover>
          </div>
        </div>
      </div>
    );
  };

  const getLogo = () => (
    <Link to="/">
      <Logo height={72} color="white" logoType="double" ariaLabel="Northern Trust logo, link to home page" />
    </Link>
  );

  return (
    <Suspense fallback={<Loader ariaLabel="Loading" size="lg" />}>
      <XPSHeader
        appTitle="Family Office Technology"
        contentComponent={headerLinks}
        displayUtilityBar={false}
        theme="dark"
        logo={getLogo}
      />
      <Modal
        alert={true}
        size="medium"
        isOpen={modalOpen}
        header={{
          heading: `${f({ id: 'home.modalHeading' })}`
        }}
        footer={{
          primaryAction: {
            label: `${f({ id: 'home.modalPrimaryBtn' })}`,
            onClick: () => {
              setModalOpen(false);
              dispatch(setUserLoggedOut(true));
              setTimeout(() => {
                logoutFn();
              }, 1000);
            }
          },
          secondaryAction: {
            label: `${f({ id: 'home.modalSecondaryBtn' })}`,
            onClick: () => setModalOpen(false)
          }
        }}
      >
        <div className="modal-content-wrap">
          <p className="margintop-0 roboto-font final-disclaimer">{f({ id: 'home.modalSubText' })}</p>
        </div>
      </Modal>
    </Suspense>
  );
};

Header.displayName = 'Header';
export default formatMessageHOC(Header);
